import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { BrandLogo } from "@/components/brand-logo";
import { authClient } from "@/lib/auth-client";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  useTitle("Choose a new password");
  const { token, error } = Route.useSearch();
  const usable = token && !error;

  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl bg-surface border border-border p-8 shadow-sm">
        <div className="flex flex-col items-center gap-2">
          <BrandLogo className="w-[220px] h-auto dark:hidden" />
          <span className="hidden dark:block"><BrandLogo variant="tile" className="w-[196px]" /></span>
        </div>
        <h1 className="mt-6 font-heading text-lg font-semibold text-foreground text-center">Choose a new password</h1>
        {usable ? (
          <ResetForm token={token} />
        ) : (
          <div className="mt-4 text-center text-sm text-muted">
            <p>This reset link has expired or was already used.</p>
            <Link href="/login" className="mt-4 inline-block text-xs text-accent-dark hover:underline">
              Back to sign in to ask for a new one
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm text-foreground outline-none focus:border-accent-dark";

function ResetForm({ token }: { token: string }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (pw !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setLoading(true);
    const { error } = await authClient.resetPassword({ newPassword: pw, token });
    setLoading(false);
    if (error) setError(error.message ?? "Could not reset the password. The link may have expired.");
    else setDone(true);
  }

  if (done) {
    return (
      <div className="mt-4 text-center text-sm text-muted">
        <p>Your password is updated. Other devices were signed out.</p>
        <Link href="/login" className="mt-4 inline-block text-xs text-accent-dark hover:underline">
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-6 space-y-3">
      <input
        type="password"
        required
        minLength={8}
        autoComplete="new-password"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        placeholder="New password"
        aria-label="New password"
        className={inputClass}
      />
      <input
        type="password"
        required
        minLength={8}
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="Confirm password"
        aria-label="Confirm password"
        className={inputClass}
      />
      <button
        type="submit"
        disabled={loading || pw.length < 8}
        className="w-full rounded-lg bg-primary hover:bg-primary-hover disabled:opacity-60 text-primary-foreground text-sm font-semibold py-2.5 transition-colors"
      >
        {loading ? "Saving..." : "Save password"}
      </button>
      <p className="text-center text-[11px] text-muted">At least 8 characters.</p>
      {error && <p className="text-xs text-red-500 text-center">{error}</p>}
    </form>
  );
}
